import { BuyNGetMMarketingCampaignConfig, MarketingCampaignDiscount, MarketingCampaignType, Offer } from '../1_types/genericTypes';
import { BNGM_PREFIX, BNGM_SLOT_SEPARATOR, OFFER_SECTION_KEY, PRODUCT_KEY_ID_SEPARATOR } from '../constants/product';
import { generateId } from './idUtils';
import { getBngmOptionListKey, isOptionListTarget } from './marketingCampaign';

export type BngmConfig = {
  numProductsPaid: number;
  numProductsDiscounted: number;
  optionListKey: string;
  discount: MarketingCampaignDiscount;
  offerId: string;
  offerLabel: string;
  menuAnchorProductKey?: string;
};

type OptionListCategory = {
  key: string;
  name: string;
  image?: string | null;
  items?: { [key: string]: { productKey?: string; name?: string; price?: number } };
};

type OptionEntry = {
  key?: string;
  productKey?: string;
  name?: string;
  label?: string;
  price?: number;
  categoryKey?: string;
  sectionKey?: string;
  slotIndex?: number;
  isFree?: boolean;
  itemRecursiveKey?: string;
  [key: string]: unknown;
};

type BasketItemCategory = {
  categoryKey?: string;
  categoryId?: string;
  categoryMultiple?: boolean;
  categoryName?: string;
  categoryPosition?: number;
  options: Record<string, OptionEntry>;
  [key: string]: unknown;
};

type BngmBasketItem = {
  fullKey: string;
  key: string;
  productKey: string;
  name: string | undefined;
  image: string | null;
  logo: string | null;
  sectionKey: string | undefined;
  sectionName: string | undefined;
  categories: string | null;
  nb: number;
  price: number;
  unitPrice: number;
  boughtPrice: number;
  items: BasketItemCategory[];
  productMarketingCampaignKey: string;
  position: number;
};

type MenuProduct = {
  key?: string;
  name?: string;
  image?: string | null;
  logo?: string | null;
  price?: number | string;
  categories?: string | null;
  position?: number;
  [key: string]: unknown;
};

export const isBngmOptionListOffer = (offer: Offer): boolean => {
  if (offer?.type !== MarketingCampaignType.BUY_N_GET_M_CHEAPEST_AT_DISCOUNT) return false;
  const target = (offer.config as BuyNGetMMarketingCampaignConfig)?.target;
  return isOptionListTarget(target);
};

export const getSyntheticProductKey = (offerId: string): string => `${BNGM_PREFIX}_${offerId}`;

export const getSyntheticCategoryKey = (offerId: string, slotIndex: number): string =>
  `${BNGM_PREFIX}_${offerId}${BNGM_SLOT_SEPARATOR}${slotIndex}`;

export const parseSyntheticCategoryKey = (key: string): { offerId: string; slotIndex: number } | null => {
  if (!key?.startsWith(`${BNGM_PREFIX}_`)) return null;
  const sepIdx = key.lastIndexOf(BNGM_SLOT_SEPARATOR);
  if (sepIdx < 0) return null;
  const offerId = key.slice(BNGM_PREFIX.length + 1, sepIdx);
  const slotIndex = parseInt(key.slice(sepIdx + BNGM_SLOT_SEPARATOR.length), 10);
  if (Number.isNaN(slotIndex)) return null;
  return { offerId, slotIndex };
};

type SectionWithProducts = {
  products?: Record<string, { categories?: string | null } | null | undefined> | null | undefined;
};

const splitProductCategoryKeys = (categories: string | null | undefined): string[] => {
  if (!categories || typeof categories !== 'string') {
    return [];
  }
  return categories
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

export const findMenuProductKeyDeclaringOptionList = (
  sections: SectionWithProducts[] | null | undefined,
  optionListKey: string
): string | undefined => {
  if (!sections?.length || !optionListKey) {
    return undefined;
  }
  for (const section of sections) {
    const products = section?.products;
    if (!products || typeof products !== 'object') {
      continue;
    }
    for (const [productKey, product] of Object.entries(products)) {
      if (!productKey.startsWith('PRODUCT_')) {
        continue;
      }
      const keys = splitProductCategoryKeys(product?.categories ?? undefined);
      const declares = keys.some(
        (k) => k === optionListKey || k.startsWith(`${optionListKey}@`)
      );
      if (declares) {
        return productKey;
      }
    }
  }
  return undefined;
};

const pickFirstMenuAnchorProductKeyFromOptionList = (optionListCategory: OptionListCategory): string | undefined => {
  const items = optionListCategory?.items;
  if (!items || typeof items !== 'object') {
    return undefined;
  }
  for (const k of Object.keys(items)) {
    const pk = items[k]?.productKey;
    if (pk && String(pk).startsWith('PRODUCT_')) {
      return pk;
    }
  }
  return undefined;
};

export const buildBngmOfferProduct = (
  offer: Offer,
  optionListCategory: OptionListCategory,
  sections?: SectionWithProducts[] | null
) => {
  const config = offer.config as BuyNGetMMarketingCampaignConfig;
  const { numProductsPaid, numProductsDiscounted, target, discount } = config;
  const optionListKey = getBngmOptionListKey(config);
  const total = (numProductsPaid ?? 0) + (numProductsDiscounted ?? 0);
  if (!optionListCategory || total <= 0) return null;
  if (!isOptionListTarget(target) || !optionListKey) return null;

  const slotPaths = Array.from({ length: total }, (_, i) => getSyntheticCategoryKey(offer.key, i)).join(',');
  const fromMenu =
    optionListKey &&
    findMenuProductKeyDeclaringOptionList(sections, optionListKey);
  const menuAnchorProductKey =
    fromMenu || pickFirstMenuAnchorProductKeyFromOptionList(optionListCategory);

  return {
    key: getSyntheticProductKey(offer.key),
    name: optionListCategory.name,
    image: optionListCategory.image ?? null,
    price: 0,
    sectionKey: OFFER_SECTION_KEY,
    categories: slotPaths,
    isBngmOffer: true,
    bngmConfig: {
      numProductsPaid,
      numProductsDiscounted,
      optionListKey,
      discount,
      offerId: offer.key,
      offerLabel: optionListCategory.name,
      ...(menuAnchorProductKey ? { menuAnchorProductKey } : {})
    } as BngmConfig
  };
};

export const buildBngmSyntheticCategories = (offer: Offer, optionListCategory: OptionListCategory) => {
  const config = offer.config as BuyNGetMMarketingCampaignConfig;
  const total = (config?.numProductsPaid ?? 0) + (config?.numProductsDiscounted ?? 0);
  if (!optionListCategory || total <= 0) return {};

  const out: Record<string, OptionListCategory & { id: string; min: number; max: number; multiple: boolean; isBngmSlot: boolean; bngmOfferId: string; bngmSlotIndex: number }> = {};
  for (let i = 0; i < total; i++) {
    const key = getSyntheticCategoryKey(offer.key, i);
    out[key] = {
      ...optionListCategory,
      key,
      id: key,
      min: 1,
      max: 1,
      multiple: false,
      name: `${optionListCategory.name} ${i + 1}`,
      isBngmSlot: true,
      bngmOfferId: offer.key,
      bngmSlotIndex: i
    };
  }
  return out;
};

type ProductItemOption = {
  categoryKey?: string;
  productKey?: string;
  slotIndex?: number;
  categoryMultiple?: boolean;
  categoryName?: string;
  options?: Record<string, OptionEntry>;
  [key: string]: unknown;
};

export const getAnchorMenuProductKeyFromBngmItems = (
  items: ProductItemOption[] | null | undefined
): string | undefined => {
  if (!Array.isArray(items)) {
    return undefined;
  }
  for (const row of items) {
    if (!row?.options || row.categoryMultiple === true) {
      continue;
    }
    for (const opt of Object.values(row.options)) {
      const pk = opt && typeof opt === 'object' ? (opt as { productKey?: string }).productKey : undefined;
      if (pk && String(pk).startsWith('PRODUCT_')) {
        return pk;
      }
    }
  }
  return undefined;
};

type ShopSection = {
  key: string;
  name?: string;
  products?: Record<string, MenuProduct>;
};

export const buildBngmBasketItems = (
  productItems: ProductItemOption[],
  bngmConfig: BngmConfig,
  shopSections: ShopSection[] | null | undefined
) => {
  if (!bngmConfig || !Array.isArray(productItems) || !productItems.length) return [];

  const slotGroups = new Map<number, { main: ProductItemOption | null; extras: ProductItemOption[] }>();

  for (const item of productItems) {
    if (!item?.categoryKey) continue;

    if (item.categoryKey.startsWith(`${BNGM_PREFIX}_`) && item.categoryKey.includes('__ITEM_')) {
      const slotPart = item.categoryKey.split('__')[0];
      const parsed = parseSyntheticCategoryKey(slotPart);
      if (parsed && parsed.offerId === bngmConfig.offerId) {
        const group = slotGroups.get(parsed.slotIndex) ?? { main: null, extras: [] };
        group.extras.push(item);
        slotGroups.set(parsed.slotIndex, group);
      }
      continue;
    }

    const parsed = parseSyntheticCategoryKey(item.categoryKey);
    if (parsed && parsed.offerId === bngmConfig.offerId) {
      const group = slotGroups.get(parsed.slotIndex) ?? { main: null, extras: [] };
      group.main = item;
      slotGroups.set(parsed.slotIndex, group);
    }
  }

  const results: (BngmBasketItem & { mergeKey: string })[] = [];

  const entries = Array.from(slotGroups.entries());
  for (const [, { main, extras }] of entries) {
    if (!main?.options) continue;

    const optionEntries = Object.entries(main.options);
    if (!optionEntries.length) continue;
    const [, selectedOption] = optionEntries[0] as [string, OptionEntry];

    const productKey = selectedOption?.productKey;
    if (!productKey) continue;

    let realProduct: MenuProduct | null = null;
    let sectionKey: string | undefined = selectedOption?.sectionKey;
    let sectionName: string | undefined;

    if (Array.isArray(shopSections)) {
      for (const sec of shopSections) {
        if (sec?.products?.[productKey]) {
          realProduct = sec.products[productKey];
          if (!sectionKey) sectionKey = sec.key;
          if (sec.key === sectionKey) sectionName = sec.name;
          break;
        }
      }
      if (!sectionName && sectionKey) {
        const sec = shopSections.find((s) => s?.key === sectionKey);
        if (sec?.name) sectionName = sec.name;
      }
    }

    const items: BasketItemCategory[] = [];

    for (const extra of extras) {
      const realCatKey = extractRealOptionCategoryFromCompositeBngmKey(extra.categoryKey);
      if (!realCatKey) continue;

      const translatedOpts: Record<string, OptionEntry> = {};
      for (const [k, v] of Object.entries(extra.options || {})) {
        translatedOpts[k] = v && typeof v === 'object' ? { ...(v as OptionEntry), categoryKey: realCatKey } : (v as OptionEntry);
      }

      items.push({
        ...extra,
        categoryKey: realCatKey,
        categoryId: realCatKey,
        options: translatedOpts
      } as BasketItemCategory);
    }

    const basePrice = parseFloat(String(realProduct?.price ?? selectedOption?.price ?? 0));
    let totalPrice = basePrice;
    for (const extra of extras) {
      for (const opt of Object.values(extra.options || {})) {
        if (opt && typeof opt === 'object' && !opt.isFree && opt.price) {
          totalPrice += parseFloat(String(opt.price));
        }
      }
    }

    const extraFingerprint = items
      .map((it) => {
        const optKeys = Object.values(it.options || {})
          .map((o) => `${o?.key ?? ''}:${o?.price ?? 0}`)
          .sort()
          .join(',');
        return `${it.categoryKey}|${optKeys}`;
      })
      .join(';');
    const mergeKey = `${productKey}::${extraFingerprint}`;

    results.push({
      mergeKey,
      fullKey: `${productKey}${PRODUCT_KEY_ID_SEPARATOR}${generateId()}`,
      key: productKey,
      productKey,
      name: selectedOption?.name ?? selectedOption?.label ?? realProduct?.name,
      image: realProduct?.image ?? null,
      logo: realProduct?.logo ?? null,
      sectionKey,
      sectionName,
      categories: realProduct?.categories ?? null,
      nb: 1,
      price: totalPrice,
      unitPrice: basePrice,
      boughtPrice: 0,
      items,
      productMarketingCampaignKey: bngmConfig.offerId,
      position: realProduct?.position ?? 0
    });
  }

  const mergedMap = new Map<string, BngmBasketItem & { mergeKey: string }>();
  for (const result of results) {
    const existing = mergedMap.get(result.mergeKey);
    if (existing) {
      existing.nb += 1;
      existing.price = Math.round((result.price * existing.nb) * 100) / 100;
    } else {
      mergedMap.set(result.mergeKey, result);
    }
  }

  return Array.from(mergedMap.values()).map(({ mergeKey: _, ...rest }): BngmBasketItem => rest);
};

type CartProduct = {
  nb?: number;
  productKey?: string;
  items?: BasketItemCategory[];
  productMarketingCampaignKey?: string;
  [key: string]: unknown;
};

export const rehydrateBngmItemsFromCart = (
  commande: Record<string, CartProduct> | null | undefined,
  bngmConfig: BngmConfig,
  optionListCategory?: OptionListCategory | null
): { items: BasketItemCategory[]; found: boolean } => {
  if (!commande || !bngmConfig) return { items: [], found: false };

  const campaignProducts = Object.values(commande).filter(
    (p) => p?.productMarketingCampaignKey === bngmConfig.offerId
  );
  if (!campaignProducts.length) return { items: [], found: false };

  const optionListItems = optionListCategory?.items ?? {};

  const merged: BasketItemCategory[] = [];
  let nextSlotIndex = 0;

  for (const cartProduct of campaignProducts) {
    const repeatCount = (cartProduct.nb as number) ?? 1;
    const productKey = cartProduct.productKey as string | undefined;

    const matchingOptionItem = productKey
      ? Object.entries(optionListItems).find(([, v]) => v?.productKey === productKey)
      : undefined;

    for (let unit = 0; unit < repeatCount; unit++) {
      const slotIndex = nextSlotIndex++;
      const syntheticKey = getSyntheticCategoryKey(bngmConfig.offerId, slotIndex);

      if (matchingOptionItem) {
        const [itemKey, itemData] = matchingOptionItem;
        const optionUuid = `rehydrated-${slotIndex}`;
        const itemRecursiveKey = `${syntheticKey}__${itemKey}`;

        merged.push({
          categoryKey: syntheticKey,
          categoryId: syntheticKey,
          categoryMultiple: false,
          categoryName: `${optionListCategory?.name ?? ''} ${slotIndex + 1}`,
          categoryPosition: slotIndex,
          options: {
            [optionUuid]: {
              key: itemKey,
              label: itemData?.name,
              name: itemData?.name,
              price: itemData?.price ?? 0,
              productKey: itemData?.productKey,
              categoryKey: syntheticKey,
              slotIndex,
              itemRecursiveKey,
              nb: 1,
              position: slotIndex
            }
          }
        });

        const cartItems = Array.isArray(cartProduct.items) ? cartProduct.items : [];
        for (const item of cartItems) {
          if (!item || !item.categoryKey?.startsWith('OPTION_')) continue;
          const compositeKey = `${itemRecursiveKey}__${item.categoryKey}`;
          const convertedOptions: Record<string, OptionEntry> = {};
          for (const [k, v] of Object.entries(item.options || {})) {
            convertedOptions[k] = v && typeof v === 'object' ? { ...(v as OptionEntry), categoryKey: compositeKey } : (v as OptionEntry);
          }
          merged.push({
            ...item,
            categoryKey: compositeKey,
            categoryId: compositeKey,
            options: convertedOptions
          });
        }
      }
    }
  }

  return { items: merged, found: merged.length > 0 };
};

const extractRealOptionCategoryFromCompositeBngmKey = (categoryKey: string): string | undefined => {
  if (!categoryKey?.startsWith(`${BNGM_PREFIX}_`) || !categoryKey.includes('__ITEM_')) {
    return undefined;
  }
  const segments = categoryKey.split('__');
  for (let i = segments.length - 1; i >= 0; i--) {
    const seg = segments[i];
    if (seg.startsWith('OPTION_')) {
      const atIdx = seg.indexOf('@');
      return atIdx >= 0 ? seg.slice(0, atIdx) : seg;
    }
  }
  return undefined;
};
